<template>
  <div>
    <!-- Ticket Price Type -->
    <b-form-group
      label-for="ticket-price-type"
    >
      <template #label>
        <h5 class="text-capitalize mb-1">
          price type
        </h5>
      </template>
      <b-form-radio-group
        id="ticket-price-type"
        v-model="ticket.price_type"
        class="mb-2"
        value-field="value"
        :options="[
          {
            name:'Paid',value:2
          },
          {
            name:'Free',value:1
          }]"
        text-field="name"
        buttons
        button-variant="outline-primary btn-pill"
      />
    </b-form-group>

    <!-- Ticket Name -->
    <text-input
      id="ticket-name"
      name="Name"
      placeholder="Name"
      rules="required|max:200"
      :text.sync="ticket.name"
    />

    <b-checkbox
      v-model="ticket.is_early_bird"
      @change="appendEarlyBird"
    >
      Early Bird
    </b-checkbox>

    <p class="my-1">
      <small>Early Bird Tickets can be helpful for any event, as it gives the event managers revenue sooner rather than later.</small>
    </p>

    <div class="row mb-2">
      <!-- Quantity -->
      <div class="col">
        <text-input
          id="ticket-quantity"
          name="Quantity Limit"
          placeholder="Quantity Limit"
          rules="required|max_value:200|integer"
          type="number"
          :text.sync="ticket.quantity"
        />
      </div>

      <!-- Price -->
      <div class="col">
        <text-input
          v-if="ticket.price_type===2"
          id="ticket-price"
          name="Price"
          placeholder="Price"
          rules="required"
          type="number"
          :text.sync="ticket.price"
        />
      </div>
    </div>

    <!-- Section -->
    <section-select
      :section.sync="ticket.section_id"
      :selected-event-id="eventId"
    />

    <!-- Time zone -->
    <div class="mb-2">
      <h5 class="text-capitalize">
        event time zone is GMT+08
      </h5>
    </div>
    <div class="row mb-3">
      <!-- Start Date -->
      <div class="col-6">
        <date-time
          id="ticket-sales-start"
          :datetime.sync="ticket.sale_start_date"
          name="Sales Start"
          placeholder="Sales Start"
          rules="required"
          :is-edit-form="isEditForm"
          :config="{ dateFormat: 'Y-m-d', minDate: 'today'}"
        />
      </div>

      <!-- Start Time -->
      <div class="col-6">
        <date-time
          id="ticket-start-time"
          :datetime.sync="ticket.sale_start_time"
          name="Start Time"
          placeholder="Start Time"
          rules="required"
          :is-edit-form="isEditForm"
          :config="{ dateFormat: 'H:i', noCalendar:true, enableTime:true,minTime:selectedEvent.start_time }"
        />
      </div>
    </div>
    <div class="row mb-3">
      <!-- End Date -->
      <div class="col-6">
        <date-time
          id="ticket-sales-end"
          :datetime.sync="ticket.sale_end_date"
          name="Sales End"
          placeholder="Sales End"
          rules="required"
          :is-edit-form="isEditForm"
          :config="{ dateFormat: 'Y-m-d', minDate: ticket.sale_start_date,maxDate:selectedEvent.value.end_date}"
        />
      </div>

      <!-- End Time -->
      <div class="col-6">
        <date-time
          id="ticket-end-time"
          :datetime.sync="ticket.sale_end_time"
          name="End Time"
          placeholder="End Time"
          rules="required"
          :is-edit-form="isEditForm"
          :config="{ dateFormat: 'H:i', noCalendar:true, enableTime:true, minTime: minEndTime, maxTime:maxEndTime}"
        />
      </div>

    </div>

    <div
      v-show="ticket.price_type===2"
      class="mb-3"
    >
      <h5 class="text-capitalize">
        Muslim Do will gain ({{ $attrs['event-tax'] }}) per ticket
      </h5>
    </div>

    <!-- Advanced Settings Dropdown -->
    <div
      v-b-toggle.collapse-1
      class="mb-4"
      @click="advancedSettingsVisible=!advancedSettingsVisible"
    >
      <span
        class="text-capitalize d-inline-block mr-5"
      >advanced setting</span>
      <feather-icon :icon="`Chevron${advancedSettingsVisible?'Up':'Down'}Icon`" />
    </div>

    <!-- Advanced Settings -->
    <b-collapse
      id="collapse-1"
      class="mt-2"
    >
      <div class="advanced-setting-inputs">
        <!-- Visibility -->
        <custom-vue-select
          id="ticket-visibility"
          label="Visibility"
          placeholder="Visibility"
          class="mb-2"
          :options="[
            {text:'Visible',value:1},
            {text:'Hidden',value:2},
          ]"
          :value-reducer="option=>option.value"
          :selected.sync="ticket.visibility"
          :text-field="option=>option.text"
        />

        <!-- Sales Channel -->
        <custom-vue-select
          id="ticket-sales-channel"
          label="Sales Channel"
          placeholder="Sales Channel"
          :value-reducer="option=>option.value"
          :options="[{
            name:'Online',value:1
          }]"
          :selected.sync="ticket.sales_channel"
        />

        <!-- Delivery Method -->
        <b-form-group
          label="Delivery Method"
          class="mb-2"
        >
          <b-radio-group
            v-model="ticket.delivery_method"
            stacked
          >
            <b-form-radio
              v-b-tooltip.hover
              title="receive a PDF confirmation with a scannable code"
              :value="1"
            >
              ETicket
            </b-form-radio>
            <b-form-radio
              v-b-tooltip.hover
              title="they still receive a confirmation mail"
              :value="2"
            >
              Will Call
            </b-form-radio>
          </b-radio-group>
        </b-form-group>

        <!-- Description -->
        <text-area-input
          id="ticket-description"
          rules="max:2500"
          name="Description"
          placeholder="Tell attendees more about this ticket"
          :text.sync="ticket.description"
        />

        <template v-if="ticket.visibility==='custom'">
          <div class="row mb-3">
            <div class="col-6">
              <date-time
                id="ticket-start-showing-on"
                :datetime.sync="ticket.visibility_start_date"
                name="Start Showing On"
                placeholder="Start Showing On"
                rules="required"
                :config="{ dateFormat: 'Y-m-d', minDate: 'today'}"
              />
            </div>
            <div class="col-3">
              <date-time
                id="ticket-start-showing-on-time"
                :datetime.sync="ticket.visibility_start_time"
                name="Start Showing On Time"
                placeholder="Start Showing On Time"
                rules="required"
                :config="{ dateFormat: 'H:i', noCalendar:true, enableTime:true, minTime: ''}"
              />
            </div>
          </div>
          <div class="row mb-3">
            <div class="col-3">
              <date-time
                id="ticket-end-showing-on"
                :datetime.sync="ticket.visibility_end_date"
                name="End Showing On"
                placeholder="End Showing On"
                rules="required"
                :config="{ dateFormat: 'Y-m-d', minDate: 'today'}"
              />
            </div>
            <div class="col-3">
              <date-time
                id="ticket-end-showing-on-time"
                :datetime.sync="ticket.visibility_end_time"
                name="End Showing On Time"
                placeholder="End Showing On Time"
                rules="required"
                :config="{ dateFormat: 'H:i', noCalendar:true, enableTime:true, minTime: ''}"
              />
            </div>
          </div>
        </template>
      </div>
    </b-collapse>
  </div>
</template>

<script>
import TextInput from '@/components/FormInputs/TextInput.vue'
import DateTime from '@/components/FormInputs/DateTime.vue'
import CustomVueSelect from '@/components/FormInputs/CustomVueSelect.vue'
import TextAreaInput from '@/components/FormInputs/TextAreaInput.vue'
import SectionSelect from './Sections/SectionSelect.vue'

export default {
  name: 'TicketInputs',
  components: {
    TextInput,
    DateTime,
    CustomVueSelect,
    TextAreaInput,
    SectionSelect,
  },
  props: {
    ticket: { type: Object, default: () => {} },
  },
  data() {
    return {
      advancedSettingsVisible: false,
    }
  },
  computed: {
    minEndTime() {
      return this.ticket.sale_start_date === this.ticket.sale_end_date ? this.$moment(`2020-01-01 ${this.ticket.sale_start_time}`).add({ minute: 5 }).format('HH:mm') : null
    },
    maxEndTime() {
      return this.ticket.sale_end_date === this.selectedEvent.value.end_date ? this.$moment(`2020-01-01 ${this.selectedEvent.value.end_time}`).subtract({ minute: 5 }).format('HH:mm') : null
    },
    eventId() {
      return Number(this.selectedEvent?.value?.id) || Number(this.$route.params.id)
    },
  },
  inject: ['isEditForm', 'selectedEvent'],
  watch: {
    'ticket.sale_start_date': function (newValue, oldValue) {
      this.resetEndDatetime(oldValue)
    },
    'ticket.sale_start_time': function (newValue, oldValue) {
      this.resetEndDatetime(oldValue)
    },
    'ticket.sale_end_date': function (newValue, oldValue) {
      this.resetEndTime(oldValue)
    },
  },
  methods: {
    appendEarlyBird(e) {
      if (e) this.$set(this.ticket, 'name', `${this.ticket.name || ''} Early Bird`)
      else this.$set(this.ticket, 'name', this.ticket.name.replace('Early Bird', ''))
    },
    resetEndDatetime(oldValue) {
      if (oldValue) {
        this.$set(this.ticket, 'sale_end_date', null)
        this.$set(this.ticket, 'sale_end_time', null)
      }
    },
    resetEndTime(oldValue) {
      if (oldValue) {
        this.$set(this.ticket, 'sale_end_time', null)
      }
    },
  },
}
</script>

<style>

</style>
